import { useState } from 'react'
import { errorMessage, updateLesson } from '../../../services/admin'
import { dictionaryRefusal, linksFromSentence, linksToWire, pruneWordLinks } from '../dictionary'
import { WordLinkInputs } from './WordLinkInputs'
import { WordNoteInputs } from './WordNoteInputs'

// ─── Edit form (inline in a lesson card) ──────────────────────────────────────

/**
 * Title and sentence text only (2026-10-13): teaching is monolingual, so the
 * Persian title, description and translation are neither shown nor sent —
 * whatever an older lesson has there stays untouched on the server.
 *
 * Since V2-p a sentence's words are linked to the shared dictionary
 * (`WordLinkInputs`); the old per-word notes are shown read-only and never
 * sent, so whatever an admin wrote before stays exactly as it is on the
 * server until `wordNotes` leaves the wire in a later release. The links
 * are always sent for every sentence in the form, so the server validates
 * them against the text as saved and never has to drop any silently —
 * `droppedLinks` in the answer is for edits made elsewhere.
 */
export function EditLessonForm({ lesson, onSaved, onCancel }) {
  const [titleEn, setTitleEn] = useState(lesson.titleEn)
  const [sentences, setSentences] = useState(
    lesson.sentences.map(s => ({
      index: s.index,
      textEn: s.textEn,
      wordNotes: s.wordNotes ?? [],
      wordLinks: linksFromSentence(s),
    })),
  )
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const setSentence = (i, k, v) =>
    setSentences(ss => { const c = [...ss]; c[i] = { ...c[i], [k]: v }; return c })

  // textEn changes prune any link whose word no longer matches its entry —
  // no fuzzy word-tracking, just drop what no longer lines up.
  const setSentenceText = (i, textEn) =>
    setSentences(ss => {
      const c = [...ss]
      c[i] = { ...c[i], textEn, wordLinks: pruneWordLinks(c[i].wordLinks, textEn) }
      return c
    })

  async function save(e) {
    e.preventDefault(); setBusy(true); setErr('')
    try {
      const patch = {
        titleEn,
        sentences: sentences.map(s => ({
          index: s.index,
          textEn: s.textEn,
          wordLinks: linksToWire(s.wordLinks),
        })),
      }
      onSaved(await updateLesson(lesson.id, patch))
    } catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false) }
  }

  const FL = 'mb-1 block text-xs font-medium text-slate-600'
  const FI = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'

  return (
    <form onSubmit={save} className="space-y-4 border-t border-slate-100 bg-slate-50/50 px-4 py-4">
      <label className="block">
        <span className={FL}>عنوان درس</span>
        <input required value={titleEn} className={FI} dir="ltr" onChange={e => setTitleEn(e.target.value)} />
      </label>

      <div>
        <span className={FL}>{sentences.length === 1 ? 'جمله (انگلیسی)' : 'جملات'}</span>
        <div className="space-y-2">
          {sentences.map((s, i) => (
            <div key={s.index} className="space-y-1.5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                {sentences.length > 1 && <span className="w-5 shrink-0 text-xs text-slate-400 sm:text-center">{s.index + 1}</span>}
                <input required value={s.textEn} placeholder="جمله انگلیسی" dir="ltr" className={`${FI} flex-1`}
                  onChange={e => setSentenceText(i, e.target.value)} />
              </div>
              <div className={`space-y-1.5 ${sentences.length > 1 ? 'ps-7' : ''}`}>
                <WordLinkInputs textEn={s.textEn} links={s.wordLinks} onChange={next => setSentence(i, 'wordLinks', next)} />
                <WordNoteInputs textEn={s.textEn} wordNotes={s.wordNotes} readOnly />
              </div>
            </div>
          ))}
        </div>
      </div>

      {err && <p className="text-sm text-red-600">{err}</p>}

      <div className="flex gap-3">
        <button type="submit" disabled={busy}
          className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
          {busy ? 'در حال ذخیره…' : 'ذخیره'}
        </button>
        <button type="button" onClick={onCancel}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          انصراف
        </button>
      </div>
    </form>
  )
}
